import { useState, useEffect, useCallback } from "react";

// Phase 2.5.3a (UX revision) — one shared source of truth for consumer whole-brand follows.
// App instantiates this ONCE and passes state down to (a) the inline Follow pill on each
// business-browse card and (b) the "Following" list. No double-fetching, no drift between
// the pill and the list. Reads the consumer token (groovie_token) itself; never touches playback.
const BUSINESS_API_URL = "https://groovie-business-api-production.up.railway.app/api/v1";

export default function useConsumerFollows() {
  const [following, setFollowing] = useState([]);          // [{company_id, business_name, scope, channels:[...]}]
  const [followedIds, setFollowedIds] = useState(new Set());
  const [feed, setFeed] = useState([]);                    // 2.5.3b: announcements/offers from followed businesses
  const [busy, setBusy] = useState(null);
  const getToken = () => localStorage.getItem("groovie_token");
  const signedIn = !!getToken();

  const reload = useCallback(async () => {
    const t = getToken();
    if (!t) { setFollowing([]); setFollowedIds(new Set()); return; }
    try {
      const r = await fetch(`${BUSINESS_API_URL}/consumer/follows`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) return;
      const d = await r.json();
      setFollowing(d.following || []);
      setFollowedIds(new Set((d.following || []).map((b) => b.company_id)));
    } catch { /* keep prior state */ }
  }, []);

  // 2.5.3b feed — degrades silently to empty on any failure (never blocks playback/UI).
  const reloadFeed = useCallback(async () => {
    const t = getToken();
    if (!t) { setFeed([]); return; }
    try {
      const r = await fetch(`${BUSINESS_API_URL}/consumer/feed`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) { console.warn(`[FOLLOW-FEED] feed fetch HTTP ${r.status} — feed stays empty, playback unaffected`); return; }
      const d = await r.json();
      setFeed(d.feed || []);
    } catch (e) { console.warn('[FOLLOW-FEED] feed fetch failed — feed stays empty, playback unaffected:', e); }
  }, []);

  // 2.5.3b scope setter (all | announcements | offers) — refreshes both follows + feed.
  const setScope = useCallback(async (companyId, scope) => {
    const t = getToken();
    if (!t) return;
    try {
      const r = await fetch(`${BUSINESS_API_URL}/consumer/follow/${companyId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
        body: JSON.stringify({ scope }),
      });
      if (r.ok) { await reload(); await reloadFeed(); }
      else { const e = await r.json().catch(() => ({})); alert(e.detail || "Failed to set scope"); }
    } catch { /* silent */ }
  }, [reload, reloadFeed]);

  useEffect(() => { reload(); reloadFeed(); }, [reload, reloadFeed]);

  const toggle = useCallback(async (companyId) => {
    if (companyId == null) return;
    const t = getToken();
    if (!t) { alert("Sign in to follow businesses"); return; }
    setBusy(companyId);
    const isFollowing = followedIds.has(companyId);
    try {
      const r = await fetch(`${BUSINESS_API_URL}/consumer/follow${isFollowing ? "/" + companyId : ""}`, {
        method: isFollowing ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
        body: isFollowing ? undefined : JSON.stringify({ company_id: companyId }),
      });
      if (r.ok) { await reload(); await reloadFeed(); }
      else { const e = await r.json().catch(() => ({})); alert(e.detail || "Failed"); }
    } catch { alert("Network error"); }
    finally { setBusy(null); }
  }, [followedIds, reload, reloadFeed]);

  return { following, followedIds, feed, toggle, setScope, busy, signedIn, reload, reloadFeed };
}
